// /src/components/donate/KofiSupportOverview.jsx

import React, { useEffect, useRef, useState } from 'react';
import DonorGemIcon from '../icons/DonorGemIcon';
import {
  resolvePerkDisplayLevel,
  supportLevelBadgeLabel,
} from '../../pages/private/admin/PrivateDocuments/donatorPerks/donatorPerksUtils';
import { openSupportChoiceChange } from './supportChoice/supportChoiceEvents';

function levelLabel(level) {
  if (level === 'supporter') return 'Supporter';
  if (level === 'patron') return 'Patron';
  if (level === 'benefactor') return 'Benefactor';
  return 'Member';
}

function formatDate(value) {
  if (!value) return '—';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString();
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="kofi-support-overview__svg">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
        d="M12 3.2 19 6.2v5.1c0 4.5-3 7.8-7 9.5-4-1.7-7-5-7-9.5V6.2L12 3.2Z"
      />
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m8.8 12.1 2.2 2.2 4.3-4.4"
      />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="kofi-support-overview__svg">
      <circle cx="12" cy="12" r="8.2" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        d="M12 7.8v4.5l3.1 1.9"
      />
    </svg>
  );
}

function GiftIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="kofi-support-overview__svg">
      <rect x="4" y="10" width="16" height="10" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path fill="none" stroke="currentColor" strokeWidth="1.7" d="M12 10v10M4 14h16" />
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        d="M12 10c-2.2 0-3.7-1.5-3.7-3.1S9.8 4 12 6.4C14.2 4 15.7 5.3 15.7 6.9S14.2 10 12 10Z"
      />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="kofi-support-overview__svg">
      <path
        fill="currentColor"
        d="M12 3.4 13.7 9h5.8l-4.7 3.4 1.8 5.6L12 14.8 7.4 18l1.8-5.6L4.5 9h5.8L12 3.4Z"
      />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="kofi-support-overview__svg">
      <circle cx="12" cy="12" r="8.2" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" d="M12 10.4v6" />
      <circle cx="12" cy="7.6" r="1" fill="currentColor" />
    </svg>
  );
}

function PerkIcon({ iconKey, level }) {
  const tone = level === 'benefactor'
    ? 'gold'
    : level === 'patron'
      ? 'purple'
      : 'blue';

  if (iconKey === 'star') {
    return (
      <span className={`kofi-support-overview__perk-icon kofi-support-overview__perk-icon--${tone}`}>
        <StarIcon />
      </span>
    );
  }
  if (iconKey === 'shield') {
    return (
      <span className={`kofi-support-overview__perk-icon kofi-support-overview__perk-icon--${tone}`}>
        <ShieldIcon />
      </span>
    );
  }
  if (iconKey === 'gift') {
    return (
      <span className={`kofi-support-overview__perk-icon kofi-support-overview__perk-icon--${tone}`}>
        <GiftIcon />
      </span>
    );
  }

  const gemVariant = iconKey === 'gold-gem'
    ? 'premium'
    : iconKey === 'purple-gem'
      ? 'subscriber'
      : 'one-time';

  return (
    <span className={`kofi-support-overview__perk-icon kofi-support-overview__perk-icon--${tone}`}>
      <DonorGemIcon className={`donor-gem-badge__gem donor-gem-badge__gem--${gemVariant}`} />
    </span>
  );
}

export default function KofiSupportOverview({
  support = null,
  rewards = [],
  className = '',
}) {
  const lifetimeLevel = levelLabel(support?.highestSupportLevelEverReached);
  const hasActive = Boolean(support?.currentSupportLevel);
  const [rewardsOpen, setRewardsOpen] = useState(false);
  const rewardsRef = useRef(null);

  useEffect(() => {
    if (!rewardsOpen) return undefined;

    const onPointerDown = (event) => {
      if (rewardsRef.current && !rewardsRef.current.contains(event.target)) {
        setRewardsOpen(false);
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [rewardsOpen]);

  useEffect(() => {
    setRewardsOpen(false);
  }, [support?.subjectUserId, rewards.length]);

  return (
    <div
      className={['kofi-support-overview', className].filter(Boolean).join(' ')}
      data-resource="public.donation-panel"
      data-action="read"
      data-route="/api/kofi/my-support-status"
      data-description="Ko-fi support overview"
    >
      <div className="kofi-support-overview__status-grid">
        <section className="kofi-support-overview__card" aria-label="Lifetime recognition">
          <header className="kofi-support-overview__card-header">
            <span className="kofi-support-overview__card-icon" aria-hidden>
              <ShieldIcon />
            </span>
            <h4 className="kofi-support-overview__card-title">Lifetime recognition</h4>
          </header>
          <p className="kofi-support-overview__card-line">
            <span>Level:</span>
            <strong>{lifetimeLevel}</strong>
            <span className="kofi-support-overview__dot" aria-hidden />
          </p>
          <p className="kofi-support-overview__card-line">
            <span>Lifetime total</span>
            <strong className="kofi-support-overview__accent">
              €{Number(support?.lifetimeTotalEur || 0).toFixed(2)}
            </strong>
          </p>
        </section>

        <section className="kofi-support-overview__card" aria-label="Active support">
          <header className="kofi-support-overview__card-header">
            <span className="kofi-support-overview__card-icon" aria-hidden>
              <ClockIcon />
            </span>
            <h4 className="kofi-support-overview__card-title">Active support</h4>
            {hasActive && support?.canChangeActiveSupport && !support?.hasPendingChoice ? (
              <button
                type="button"
                className="kofi-support-overview__change"
                onClick={openSupportChoiceChange}
                data-resource="public.donation-panel"
                data-action="execute"
                data-route="/api/kofi/my-support-choice"
                data-description="Change active support level"
              >
                Change
              </button>
            ) : null}
          </header>
          {hasActive ? (
            <>
              <p className="kofi-support-overview__card-line">
                <span>Level:</span>
                <strong>{levelLabel(support.currentSupportLevel)}</strong>
              </p>
              <p className="kofi-support-overview__card-line">
                <span>Until</span>
                <strong>{formatDate(support.currentSupportUntil)}</strong>
              </p>
            </>
          ) : (
            <p className="kofi-support-overview__card-empty">
              No active support currently.
              <span className="kofi-support-overview__dash" aria-hidden>—</span>
            </p>
          )}
        </section>
      </div>

      {support?.activeSubscription?.isActiveSubscriber ? (
        <a
          href={support.activeSubscription.manageUrl}
          className="kofi-support-overview__link"
          target="_blank"
          rel="noopener noreferrer"
          data-resource="public.donation-panel"
          data-action="read"
        >
          Manage your subscription on Ko-fi
        </a>
      ) : null}

      {rewards.length > 0 ? (
        <div className="kofi-support-overview__next-reward" ref={rewardsRef}>
          <button
            type="button"
            className={[
              'kofi-support-overview__next-reward-chip',
              rewardsOpen ? 'kofi-support-overview__next-reward-chip--open' : '',
            ].filter(Boolean).join(' ')}
            onClick={() => setRewardsOpen((open) => !open)}
            aria-expanded={rewardsOpen}
            aria-controls="kofi-next-reward-tooltip"
            data-resource="public.donation-panel"
            data-action="read"
            data-route="/api/kofi/my-support-status"
            data-description="Toggle next reward tooltip"
          >
            <span className="kofi-support-overview__next-reward-chip-icon" aria-hidden>
              <GiftIcon />
            </span>
            Next reward
          </button>

          {rewardsOpen ? (
            <div
              id="kofi-next-reward-tooltip"
              className="kofi-support-overview__next-reward-tooltip"
              role="tooltip"
            >
              <ul className="kofi-support-overview__rewards-list">
                {rewards.map((perk) => {
                  const level = resolvePerkDisplayLevel(perk);
                  return (
                    <li
                      key={perk.id || `${perk.title}-${perk.sortOrder}`}
                      className="kofi-support-overview__reward"
                    >
                      <PerkIcon iconKey={perk.iconKey} level={level} />
                      <div className="kofi-support-overview__reward-main">
                        <p className="kofi-support-overview__reward-title">{perk.title}</p>
                        <p className={`kofi-support-overview__reward-tier kofi-support-overview__reward-tier--${level}`}>
                          {supportLevelBadgeLabel(level)}
                        </p>
                      </div>
                      <p className="kofi-support-overview__reward-req">
                        {perk.requirementLabel || 'Active support'}
                      </p>
                      <p className="kofi-support-overview__reward-desc">
                        {perk.description || '—'}
                      </p>
                    </li>
                  );
                })}
              </ul>
              <p className="kofi-support-overview__footnote">
                <span className="kofi-support-overview__footnote-icon" aria-hidden>
                  <InfoIcon />
                </span>
                Perks are purely visual and never provide any in-game advantage.
              </p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
